/**
 * 文件主要负责生成不同类型的message配置，ui组件也直接集成，后续新增可直接在此处处理
 */
import { IMsg } from '_c/typings/model/im';
import { MsgL2Type } from '_c/typings/proto';
import { Component } from 'vue';
import { ISimpleUser } from '_c/typings/model/user';
import { logger } from '_c/util/logger';
import { TOperationSetting } from '_c/typings/model/chat/message-operate';

const TAG = 'GenerateMessageConfig';

/**
 * 基础消息配置，主要应用于带有头像昵称的消息类型
 */
interface MessageConfigBase {
  // 消息顺序，唯一id
  messageSeq: string
  // 发送者信息
  senderUserInfo?: ISimpleUser
}

export interface IMessageViewConfig {
  // 是否需要基础样式（包括头像昵称等），默认需要（系统消息，撤回消息等不需要）
  needBaseStyle: boolean,
  // 是否可以显示reaction消息，默认支持
  canShowReaction: boolean,
  // 是否可以修改reaction消息（增加/删除），默认支持，canShowReaction 为 false 时，此项无效
  canReviseReaction: boolean,
  // 是否可以显示topic消息，默认支持
  canShowTopic: boolean,
  // 是否显示消息状态
  canShowStatus: boolean,
  // 是否显示自定义的链接，比如 @xxx、#xxx
  canShowCustomLink: boolean,
  // 支持的操作列表
  supportOperateList?: Array<TOperationSetting>,
}

interface MessageConfigDefault extends IMessageViewConfig, MessageConfigBase {
  messageData: IMsg,
  // 组件内属性，默认为空对象
  props: Record<string, unknown>,
  // 是否合法，默认为true
  isValid: boolean,
  // 是否是未知消息或者不合法消息，此类消息不响应事件处理
  isUnknown: boolean,
}

/**
 * 标准消息配置，扩展自基础消息配置，带有不同消息类型定制化的参数，默认值在generateDefaultMessageConfig中
 */
export interface MessageConfig extends MessageConfigDefault {
  // 展示组件，默认为未知组件
  component: Component,
}

interface MessageConfigGenerateOptions extends Partial<MessageConfig>{
  // 展示组件，默认为未知组件
  component: Component,
}

/**
 * 生成通用的消息配置
 * @param messageData
 */
const generateCommonMessageConfig = (messageData: IMsg): MessageConfigBase => ({
  messageSeq: messageData?.origin?.sequence?.toString() ?? '',
  senderUserInfo: messageData.senderUserInfo,
});


/**
 * 生成默认的消息配置
 * @param messageData
 */
export const generateDefaultMessageConfig = (messageData: IMsg): MessageConfigDefault => ({
  ...generateCommonMessageConfig(messageData),
  messageData,
  props: {},
  needBaseStyle: true,
  canShowReaction: true,
  canReviseReaction: true,
  canShowTopic: true,
  canShowStatus: true,
  canShowCustomLink: true,
  isValid: true,
  isUnknown: false,
  supportOperateList: [],
});

/**
 * 生成未知消息配置
 * @param messageData
 */
let generateUnknownMessageConfig: (message: IMsg) => MessageConfig;

/**
 * 生成不合法消息配置
 * @param messageData
 */
let generateInvalidMessageConfig: (message: IMsg) => MessageConfig;

/**
 * 生成撤回消息配置
 * @param messageData
 */
let generateRevokeMessageConfig: (message: IMsg) => MessageConfig;

/**
 * 消息配置表
 */
const messageConfigGenerateFuncMap: Partial<Record<MsgL2Type, (
  message: IMsg,
  messageViewConfig?: IMessageViewConfig,
) => MessageConfig>> = {};

/**
 * 设置不合法消息配置
 * @param options
 */
const setInvalidMessageConfig = (options:
MessageConfigGenerateOptions | ((message: IMsg) => MessageConfigGenerateOptions)): void => {
  generateInvalidMessageConfig = (message: IMsg) => {
    let opts: MessageConfigGenerateOptions;
    if (typeof options === 'function') {
      opts = options(message);
    } else {
      opts = { ...options };
    }
    return {
      ...generateDefaultMessageConfig(message),
      ...opts,
    };
  };
};

/**
 * 设置未知消息配置
 * @param options
 */
const setUnknownMessageConfig = (options:
MessageConfigGenerateOptions | ((message: IMsg) => MessageConfigGenerateOptions)): void => {
  generateUnknownMessageConfig = (message: IMsg) => {
    let opts: MessageConfigGenerateOptions;
    if (typeof options === 'function') {
      opts = options(message);
    } else {
      opts = { ...options };
    }
    return {
      ...generateDefaultMessageConfig(message),
      ...opts,
    };
  };
};

/**
 * 设置撤回消息配置
 * @param options
 */
const setRevokeMessageConfig = (options:
MessageConfigGenerateOptions | ((message: IMsg) => MessageConfigGenerateOptions)): void => {
  generateRevokeMessageConfig = (message: IMsg) => {
    let opts: MessageConfigGenerateOptions;
    if (typeof options === 'function') {
      opts = options(message);
    } else {
      opts = { ...options };
    }
    return {
      ...generateDefaultMessageConfig(message),
      ...opts,
    };
  };
};

/**
 * 设置其他消息配置
 * @param l2Type
 * @param options
 */
const setMessageGenerateFunc = (
  l2Type: MsgL2Type,
  options: MessageConfigGenerateOptions | ((
    message: IMsg,
    messageViewConfig?: IMessageViewConfig,
  ) => MessageConfigGenerateOptions),
): void => {
  if (l2Type) {
    messageConfigGenerateFuncMap[l2Type] = (message, messageViewConfig) => {
      let opts: MessageConfigGenerateOptions;
      if (typeof options === 'function') {
        opts = options(message, messageViewConfig);
      } else {
        opts = { ...options };
      }
      return {
        ...generateDefaultMessageConfig(message),
        ...opts,
      };
    };
  } else {
    logger.warn(TAG, `setMessageGenerateFunc fail, l2Type: ${l2Type}`);
  }
};

const getMessageConfig = (message: IMsg, messageViewConfig?: IMessageViewConfig): MessageConfig => {
  // 撤回消息
  if (message.origin?.isRevoked) {
    if (typeof generateRevokeMessageConfig === 'function') {
      return generateRevokeMessageConfig(message);
    }
    throw `${TAG}, generate revoke message config fail`;
  }
  const fn = messageConfigGenerateFuncMap[message.l2Type];
  if (fn) {
    try {
      const messageConfig = fn.call(undefined, message, messageViewConfig);
      if (messageConfig.isValid) {
        return messageConfig;
      }
      return generateInvalidMessageConfig(message);
    } catch (err) {
      logger.error(TAG, 'generate message config fail, err', err);
    }
  }
  if (typeof generateUnknownMessageConfig === 'function') {
    return generateUnknownMessageConfig(message);
  }
  logger.error(TAG, 'generate unknown config fail');
  throw `${TAG}, generate unknown config fail`;
};

export {
  getMessageConfig,
  setUnknownMessageConfig,
  setInvalidMessageConfig,
  setRevokeMessageConfig,
  setMessageGenerateFunc,
};
